// Top-of-page strip showing what version of code + schema is live in
// each env. Combines migration version (from /api/admin/env-status)
// with per-app commit SHA + deploy age (from /api/admin/deploys data
// already loaded by the page).
//
// Renders three columns (Dev / Staging / Prod), each compact enough
// to fit above the uptime row without dominating the screen. Highlights
// when migration version drifts between envs.

interface EnvRow {
  env: 'dev' | 'staging' | 'prod';
  configured: boolean;
  migrationVersion: string | null;
  migrationsApplied: number;
  error?: string;
}

interface EnvStatusData {
  configured: boolean;
  reason?: string;
  envs: EnvRow[];
  drift: boolean;
}

interface DeployState {
  app: 'hub' | 'persona_builder' | 'persona_studio';
  env: 'dev' | 'staging' | 'prod';
  state: string;
  commitSha: string | null;
  readyAt: number | null;
  createdAt: number | null;
}

interface Props {
  envStatus: EnvStatusData;
  deploys: DeployState[];
}

const ENV_LABEL: Record<EnvRow['env'], string> = {
  dev: 'Dev',
  staging: 'Staging',
  prod: 'Prod',
};

const APP_LABEL: Record<DeployState['app'], string> = {
  hub: 'Hub',
  persona_builder: 'Builder',
  persona_studio: 'Studio',
};

const APPS: DeployState['app'][] = ['hub', 'persona_builder', 'persona_studio'];

export function EnvStatusStrip({ envStatus, deploys }: Props) {
  if (!envStatus.configured) {
    // Quiet "not configured" hint — the rest of the dashboard still works.
    return (
      <section className="border border-slate-800 rounded-lg p-3 text-[10px] text-slate-500">
        <span className="uppercase tracking-widest font-bold mr-2">Env status</span>
        <span className="italic">
          {envStatus.reason || 'SUPABASE_MGMT_TOKEN not set; migration version unknown.'}
        </span>
      </section>
    );
  }

  return (
    <section
      className={`border rounded-lg overflow-hidden ${
        envStatus.drift ? 'border-amber-800/50' : 'border-slate-800'
      }`}
    >
      <div
        className={`px-4 py-1.5 border-b flex items-center justify-between gap-3 ${
          envStatus.drift
            ? 'bg-amber-950/20 border-amber-800/40'
            : 'bg-slate-900/40 border-slate-800'
        }`}
      >
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-300">
          Environment status
        </h3>
        {envStatus.drift && (
          <span className="text-[10px] uppercase tracking-widest font-bold text-amber-300">
            ⚠ migration drift
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-800">
        {envStatus.envs.map((row) => (
          <EnvColumn
            key={row.env}
            row={row}
            deploys={deploys.filter((d) => d.env === row.env)}
          />
        ))}
      </div>
    </section>
  );
}

function EnvColumn({ row, deploys }: { row: EnvRow; deploys: DeployState[] }) {
  return (
    <div className="bg-slate-950/40 p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-300">
          {ENV_LABEL[row.env]}
        </div>
        <div className="text-[9px] font-mono text-slate-500" title="latest applied migration">
          {row.migrationVersion ? (
            <>
              <span className="text-slate-400">migration </span>
              <span className="text-indigo-300">{row.migrationVersion}</span>
            </>
          ) : (
            <span className="italic">{row.error ? 'error' : 'no migrations'}</span>
          )}
        </div>
      </div>
      <ul className="space-y-0.5">
        {APPS.map((app) => {
          const d = deploys.find((dd) => dd.app === app);
          return <AppLine key={app} app={app} deploy={d} />;
        })}
      </ul>
    </div>
  );
}

function AppLine({
  app,
  deploy,
}: {
  app: DeployState['app'];
  deploy?: DeployState;
}) {
  const commitShort = deploy?.commitSha ? deploy.commitSha.slice(0, 7) : null;
  const age =
    deploy?.readyAt ? formatRel(deploy.readyAt)
    : deploy?.createdAt ? formatRel(deploy.createdAt)
    : null;
  const stale = deploy?.readyAt && Date.now() - deploy.readyAt > 30 * 24 * 60 * 60 * 1000;
  return (
    <li className="flex items-center justify-between gap-2 text-[11px]">
      <span className="text-slate-400 w-14">{APP_LABEL[app]}</span>
      <div className="flex items-center gap-2 text-[10px] font-mono">
        {commitShort ? (
          <span className="text-slate-300">{commitShort}</span>
        ) : (
          <span className="text-slate-600 italic">—</span>
        )}
        <span className={stale ? 'text-amber-400' : 'text-slate-500'}>{age ?? '—'}</span>
      </div>
    </li>
  );
}

function formatRel(ms: number): string {
  const diff = Math.max(0, Date.now() - ms);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}
